import type { DataProvider, Identifier } from "ra-core";
import type { Attendee } from "@/components/gavion/domain/granola";
import type {
  ApiScope,
  WebhookEvent,
} from "@/components/gavion/domain/integrations";

export type MeetingNoteInput = {
  summaryMarkdown: string;
  title: string | null;
  startedAt: string;
  companyId: Identifier | null;
  dealId: Identifier | null;
  contactId: Identifier | null;
  shareUrl: string | null;
  attendees: Attendee[];
};
export type MeetingNoteResult = {
  id: Identifier;
  created: boolean;
  matchStatus: "matched" | "unmatched";
};
export type NoteTarget = {
  companyId: Identifier | null;
  dealId: Identifier | null;
  contactId: Identifier | null;
};
export type ApiKeyCreated = {
  uid: string;
  name: string;
  keyPrefix: string;
  scopes: ApiScope[];
  plainKey: string;
};
export type WebhookEndpointCreated = {
  uid: string;
  url: string;
  events: WebhookEvent[];
  secret: string;
};
export type InboundStatus = {
  enabled: boolean;
  secretSetAt: string | null;
  lastReceivedAt: string | null;
};
/** Granola API mode (spec 07 §2): the rep's own key; the key itself never comes back to the browser. */
export type GranolaConnectionStatus = {
  status: "none" | "connected" | "invalid" | "trial_ended" | "error";
  connectedAt: string | null;
  lastSyncAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
};
export type NoteSuggestion = {
  companyId: number;
  companyName: string;
  dealId: number | null;
  contactId: number | null;
  reason: "booked_demo" | "phone" | "attendee" | "title";
  demoAt: string | null;
};
export interface IntegrationMethods {
  granolaAddManualNote(input: MeetingNoteInput): Promise<MeetingNoteResult>;
  granolaAssociateNote(noteId: Identifier, target: NoteTarget): Promise<void>;
  granolaIgnoreNote(noteId: Identifier): Promise<void>;
  /** Undo of ignore: back to "matched" when the note still has a target, else "unmatched". */
  granolaUnignoreNote(
    noteId: Identifier,
  ): Promise<{ matchStatus: "matched" | "unmatched" }>;
  /** granola partners: the lead an « À associer » note probably belongs to (meeting_note_suggestion), or null. */
  granolaNoteSuggestion(noteId: Identifier): Promise<NoteSuggestion | null>;
  granolaCreateTasks(
    noteId: Identifier,
    items: string[],
    dueAt: string | null,
  ): Promise<{ created: number; skipped: number }>;
  /** Granola API mode: the caller's own connection. */
  granolaStatus(): Promise<GranolaConnectionStatus>;
  /** Validated by one Granola call before it is stored in Vault; errors are stable codes. */
  granolaConnect(apiKey: string): Promise<GranolaConnectionStatus>;
  granolaDisconnect(): Promise<void>;
  /** « Synchroniser maintenant »: at most once a minute per rep (granola_sync_too_soon otherwise). */
  granolaSyncNow(): Promise<GranolaConnectionStatus>;
  apiKeyCreate(name: string, scopes: ApiScope[]): Promise<ApiKeyCreated>;
  apiKeyRevoke(uid: string): Promise<void>;
  webhookEndpointCreate(
    url: string,
    events: WebhookEvent[],
  ): Promise<WebhookEndpointCreated>;
  webhookEndpointSetEnabled(uid: string, enabled: boolean): Promise<void>;
  webhookEndpointDelete(uid: string): Promise<void>;
  inboundStatus(): Promise<InboundStatus>;
  inboundRotateSecret(): Promise<{ secret: string }>;
  inboundSetEnabled(enabled: boolean): Promise<void>;
}
export type IntegrationDataProvider = DataProvider & IntegrationMethods;
export class IntegrationApiError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = "IntegrationApiError";
  }
}
/** Stable code of an IntegrationApiError or a P0001 RPC error; anything else → "unknown". */
export const errorCodeOf = (e: unknown): string => {
  if (e instanceof IntegrationApiError) return e.code;
  const m = (e as { message?: unknown })?.message;
  return typeof m === "string" && /^[a-z_]{2,40}$/.test(m) ? m : "unknown";
};
export const functionsBaseUrl = (): string =>
  `${import.meta.env.VITE_SUPABASE_URL ?? ""}/functions/v1`;
export const inboundWebhookUrl = (): string =>
  `${functionsBaseUrl()}/gavion-api/v1/webhooks/inbound/generic`;
export const apiBaseUrl = (): string => `${functionsBaseUrl()}/gavion-api/v1`;

// GAVION batch 3: M365Api port
export type MicrosoftFeature =
  | "mail_sync"
  | "send"
  | "calendar"
  | "teams_meetings"
  | "archive";
export type MicrosoftStatus = {
  status: "connected" | "needs_reauth" | "error" | "disconnected" | "none";
  scopes: string[];
  missing: MicrosoftFeature[];
  externalEmail: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  adminConsentUrl: string | null;
};
export type TeamsChoice = { id: string; name: string };
export type TeamsConfig = {
  teamId: string | null;
  teamName: string | null;
  channelId: string | null;
  channelName: string | null;
  posterSalesId: number | null;
  webhookConfigured: boolean;
};
export type ComposeAttachment = {
  name: string;
  contentType: string;
  contentBase64: string;
  size: number;
  /** GAVION template attachments: added by this template (picking another one replaces them); never sent. */
  fromTemplate?: number;
};
export type SendEmailInput = {
  idempotencyKey: string;
  to: string[];
  cc: string[];
  subject: string;
  html: string;
  companyId?: number | null;
  contactId?: number | null;
  dealId?: number | null;
  templateId?: number | null;
  /** GAVION ai-email: the AI draft this email came from, and whether the rep changed it (spec §4, §6). */
  aiDraftId?: number | null;
  aiDraftEdited?: boolean;
  /** GAVION ai-email (review M6): the id is an on-demand row; the edge then never blocks on a failed read of it. */
  aiDraftOnDemand?: boolean;
  attachments: ComposeAttachment[];
};
export type DemoBookInput = {
  dealId: number;
  startAt: string;
  durationMin: 30 | 45 | 60;
  attendeeEmail?: string | null;
  idempotencyKey: string;
};
export type DemoResult = {
  status: "created" | "moved" | "unchanged";
  eventId: string;
  joinUrl: string | null;
};
export type MessageBodyView = {
  contentType: "html" | "text";
  content: string;
  attachments: {
    id: string;
    name: string;
    size: number;
    contentType: string;
  }[];
};
// GAVION full-email: the CRM's stored copy of a message (read with the user's session; the read rule is the server's)
export type StoredAttachmentStatus =
  | "pending"
  | "stored"
  | "skipped_inline"
  | "skipped_type"
  | "skipped_size"
  | "skipped_cap"
  | "skipped_item"
  | "failed";
export type StoredMessageView = {
  status:
    | "pending"
    | "fetching"
    | "stored"
    | "partial"
    | "gone"
    | "private"
    | "failed"
    | "unreachable";
  bodyType: "html" | "text" | null;
  bodyHtml: string | null;
  bodyText: string | null;
  truncated: boolean;
  fetchedAt: string | null;
  attachments: {
    id: number;
    name: string;
    sizeBytes: number;
    status: StoredAttachmentStatus;
    storagePath: string | null;
  }[];
};
export interface M365Api {
  microsoftStatus(): Promise<MicrosoftStatus>;
  startMicrosoftConnect(): Promise<{ url: string }>;
  storeMicrosoftTokens(t: {
    providerToken: string;
    providerRefreshToken: string;
  }): Promise<MicrosoftStatus>;
  disconnectMicrosoft(): Promise<void>;
  teamsConfig(): Promise<TeamsConfig>;
  listTeams(): Promise<TeamsChoice[]>;
  listChannels(teamId: string): Promise<TeamsChoice[]>;
  saveTeamsChannel(i: {
    teamId: string;
    teamName: string;
    channelId: string;
    channelName: string;
  }): Promise<TeamsConfig>;
  setTeamsWebhook(url: string | null): Promise<TeamsConfig>;
  /** sales.prefs.email_signature_html of the current user ("" when unset). */
  getSignature(): Promise<string>;
  saveSignature(html: string): Promise<void>;
  sendEmail(
    i: SendEmailInput,
  ): Promise<{ messageId: number; duplicate?: boolean }>;
  messageBody(emailMessageId: number): Promise<MessageBodyView>;
  /**
   * Object URL (`blob:`) of the downloaded attachment. The caller owns it and must call
   * `URL.revokeObjectURL(url)` once the download has started (MessageBodyDialog revokes after 1 s).
   */
  attachmentUrl(emailMessageId: number, attachmentId: string): Promise<string>;
  /** GAVION full-email: the stored copy the user may read, else null (not stored yet, or not his to read). */
  storedMessage(emailMessageId: number): Promise<StoredMessageView | null>;
  /** GAVION full-email: a 60-second signed download URL of a stored attachment. */
  storedAttachmentUrl(path: string, name: string): Promise<string>;
  bookDemo(i: DemoBookInput): Promise<DemoResult>;
  rescheduleDemo(i: {
    dealId: number;
    startAt: string;
    durationMin: 30 | 45 | 60;
  }): Promise<DemoResult>;
  cancelDemo(i: { dealId: number; comment?: string | null }): Promise<void>;
  /** The deal was reassigned away from the rep whose Outlook holds the demo: a new invite from the deal owner's mailbox. */
  takeoverDemo(i: {
    dealId: number;
    startAt: string;
    durationMin: 30 | 45 | 60;
    idempotencyKey: string;
  }): Promise<DemoResult>;
}
export class M365ApiError extends Error {
  readonly code: string;
  readonly details?: Record<string, unknown>;
  /** Seconds from the server's Retry-After header, when it sent one (throttling, transient 503). */
  readonly retryAfter?: number;
  constructor(
    code: string,
    details?: Record<string, unknown>,
    opts?: { retryAfter?: number },
  ) {
    super(code);
    this.name = "M365ApiError";
    this.code = code;
    this.details = details;
    if (opts?.retryAfter !== undefined) this.retryAfter = opts.retryAfter;
  }
}
